import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface DownloadAttachmentParams {
  emailAccountId: string;
  messageId: string;
  attachmentId: string;
  filename: string;
}

export function useDownloadAttachment() {
  return useMutation({
    mutationFn: async ({ emailAccountId, messageId, attachmentId, filename }: DownloadAttachmentParams) => {
      const { data, error } = await supabase.functions.invoke('download-gmail-attachment', {
        body: {
          email_account_id: emailAccountId,
          message_id: messageId,
          attachment_id: attachmentId,
        },
      });

      if (error) throw error;
      if (!data) throw new Error('No response from server');
      if (!data.data) throw new Error('No attachment data in response');

      // Convert base64 to blob (data is already converted from base64url by edge function)
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.mimeType || 'application/octet-stream' });

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { filename };
    },
    onSuccess: (data) => {
      toast({
        title: 'Download complete',
        description: `${data.filename} has been downloaded.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Download failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
